import React, { useEffect, useState } from 'react';
import { useFetchCompanyProducts } from './hooks/useFetchCompanyProducts';
import { useFetchProductSubmissionStatuses } from './hooks/useFetchProductSubmissionStatuses';
import { selectActiveCompany } from '@common/features/user/selectors';
import { Button, ButtonTypes, SearchInput } from '@common/components';
import DatapharmTable from '@components/DatapharmTable/DatapharmTable';
import { prepareProductsColumns, useProductColumnSorting } from './publishedProductListTableSetup';
import useAppNavigation from '../../routing/useAppNavigation';
import * as Styled from './styles';
import { PageTitle, TableOverflowContainer } from '@common/styles';
import { useSearch } from '@common/components/SearchInput/useSearch';
import { FilteringSection } from '@common/components/FiltersDropdown/styles';
import { CheckboxFilter, FiltersDropdown, YesNoFilter } from '@common/components/FiltersDropdown';
import { useProductsListFilters } from '@common/features/product/hooks/useProductsListFilters';
import { useUnknownProductGroup } from '../CreateSubmission/hooks/useUnknownProductGroup';
import UnknownProductGroupPopup from '../../components/Submissions/UnknownProductGroupPopup';
import { useInfiniteLoader } from '@hooks/useInfiniteLoader';
import theme from '@common/theme';
import { NotifyModal } from '@common/components/NotifyModal';
import { useProductUpdateInterception } from '../../features/productGroup/hooks/useProductUpdateInterception';
import { Id, SearchMode } from '@common/types';
import { useAppSelector } from '@common/hooks/redux';

const Products = () => {
  const { goToPublishedProductGroup, goToProductGroup } = useAppNavigation();
  const activeCompany = useAppSelector(selectActiveCompany);
  const [searchMode, setSearchMode] = useState<SearchMode>(SearchMode.General);
  const { searchPhrase, displayPhrase, searchError, handleChangeSearch, handleClearSearch } = useSearch({ searchMode });
  const { recordLimit, increaseOffset, resetOffset, offset, initialRecordOffset } = useInfiniteLoader();
  const { sortingQuery, customSorting } = useProductColumnSorting();
  const { isUnknownGroupStatusOpen, handleShowUnknownGroup, handleCloseUnknownGroup, isProductGroupUnknown } =
    useUnknownProductGroup();

  const { showSubmissionLimitModal, closeSubmissionLimitModal, handleUpdateWithActiveSubmissions } =
    useProductUpdateInterception();

  const {
    filtersQueryString,
    filtersDropdownProps,
    blackTriangleFilterProps,
    legalCategoryFilterProps,
    productTypeFilterProps,
    productStatusFilterProps,
  } = useProductsListFilters();

  const { companyProducts, isFetchingCompanyProducts, totalCount, isLoadingCompanyProducts } = useFetchCompanyProducts(
    activeCompany?.id!,
    filtersQueryString,
    searchPhrase,
    sortingQuery,
    recordLimit,
    offset,
    offset === initialRecordOffset,
    searchMode,
  );

  const { handleUpdate } = useFetchProductSubmissionStatuses();

  const [selectedGroupState, setSelectedGroupState] = useState({ visible: false, productGroupId: '', newSubmissionData: {} });
  const [draftSubmissionId, setDraftSubmissionId] = useState<number>();

  const handleSearchModeChange = (checked: boolean) => {
    setSearchMode(checked ? SearchMode.ProductId : SearchMode.General);
    handleClearSearch();
  };

  const gotoNewSubmission = () => {
    if (!isProductGroupUnknown(selectedGroupState.productGroupId)) {
      handleUpdate(+selectedGroupState.productGroupId, selectedGroupState.newSubmissionData);
      return;
    }

    if (selectedGroupState.visible) {
      setSelectedGroupState({ productGroupId: '', visible: false, newSubmissionData: {} });
    }

    handleShowUnknownGroup();
  };

  useEffect(() => {
    resetOffset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersQueryString, searchPhrase, sortingQuery, searchMode]);

  const handleDetailsClick = (productGroupId: string) => {
    if (isProductGroupUnknown(productGroupId)) handleShowUnknownGroup();
    else goToPublishedProductGroup(productGroupId);
  };

  const handleUpdateClick = (productGroupId: number, newSubmissionData: any) => {
    const callbacks = {
      draftSubmissionCallback: (submissionId: number) => setDraftSubmissionId(submissionId),
      noActiveSubmissionCallback: () =>
        setSelectedGroupState({ visible: true, productGroupId: productGroupId.toString(), newSubmissionData: newSubmissionData }),
      activeSubmissionCallback: () => {},
    };
    handleUpdateWithActiveSubmissions({ productGroupId, callbacks });
  };

  const columns = prepareProductsColumns(handleUpdateClick, handleDetailsClick);

  const shouldShowResultsCount = !(isFetchingCompanyProducts || isLoadingCompanyProducts);
  const shouldShowLoadMoreButton =
    companyProducts.length !== totalCount &&
    !(offset === initialRecordOffset && (isFetchingCompanyProducts || isLoadingCompanyProducts));

  return (
    <Styled.ProductsWrap>
      <UnknownProductGroupPopup isOpen={isUnknownGroupStatusOpen} handleClose={handleCloseUnknownGroup} zIndex={999}/>
      <PageTitle>Products</PageTitle>
      <FilteringSection>
        <Styled.SearchSection>
          <SearchInput
            id="allProductsSearch"
            name="Search"
            value={displayPhrase}
            onChange={handleChangeSearch}
            onClearInput={handleClearSearch}
            error={searchError}
          />
          <Styled.SearchModeWrapper>
            <Styled.RadioLabel>
              <input
                type="checkbox"
                name="searchMode"
                checked={searchMode === SearchMode.ProductId}
                onChange={(e) => handleSearchModeChange(e.target.checked)}
              />
              by product ID
            </Styled.RadioLabel>
          </Styled.SearchModeWrapper>
        </Styled.SearchSection>
        <FiltersDropdown {...filtersDropdownProps}>
          <YesNoFilter {...blackTriangleFilterProps} />
          <CheckboxFilter {...legalCategoryFilterProps} />
          <CheckboxFilter {...productTypeFilterProps} />
          <CheckboxFilter {...productStatusFilterProps} />
        </FiltersDropdown>
      </FilteringSection>
      <Styled.TableWrapper>
        {shouldShowResultsCount && <Styled.ResultsCountText>{totalCount} search results</Styled.ResultsCountText>}
        <TableOverflowContainer>
          <DatapharmTable
            documents={companyProducts}
            columns={columns}
            enableSorting={true}
            customSorting={customSorting}
            showLoader={(isFetchingCompanyProducts || isLoadingCompanyProducts) && offset === initialRecordOffset}
            data-testid="products-table"
          />
        </TableOverflowContainer>
        {shouldShowLoadMoreButton && (
          <Styled.LoadMoreButtonWrapper>
            <Button
              color={theme.colors.mediumBlue}
              type={ButtonTypes.TEXT}
              onClick={increaseOffset}
              isLoading={isLoadingCompanyProducts || isFetchingCompanyProducts}
            >
              Load more
            </Button>
          </Styled.LoadMoreButtonWrapper>
        )}
      </Styled.TableWrapper>
      <NotifyModal
        isOpen={selectedGroupState.visible}
        onConfirm={() => {
          gotoNewSubmission();
        }}
        onClose={() => {
          setSelectedGroupState({ productGroupId: '', visible: false, newSubmissionData: {} });
        }}
        title="Confirm draft submission"
        width="700px"
        zIndex={998}
      >
        <p>This action will create a draft submission, please confirm if you want to continue.</p>
      </NotifyModal>
      <NotifyModal cancelText="Close" isOpen={showSubmissionLimitModal} onClose={closeSubmissionLimitModal} width="32rem">
        <p>
          <b>
            An in progress submission already exists for this product group - You can request changes to this submission via the
            submissions screen.
          </b>
        </p>
      </NotifyModal>
      <NotifyModal
        isOpen={!!draftSubmissionId}
        onConfirm={() => {
          goToProductGroup(draftSubmissionId as Id);
        }}
        onClose={() => {
          setDraftSubmissionId(undefined);
        }}
        title="Existing draft submission"
        width="700px"
      >
        <p>You will be redirected to an existing draft submission, please confirm if you want to continue.</p>
      </NotifyModal>
    </Styled.ProductsWrap>
  );
};

export default Products;
